import React, { useEffect, useMemo, useRef } from "react";
import { Modal, Input, Button, Spin, Tag, InputRef } from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import styles from "./styles.module.scss";
import classNames from "classnames";

interface SearchResultItem {
  resident: { id: number; name: string; status?: number };
  device: { name: string };
}

interface ListData {
  data?: { data: SearchResultItem[] };
  isLoading: boolean;
}

interface SearchPatientModalProps {
  isOpen: "global" | "bedExit" | null;
  onClose: () => void;
  searchValue: string;
  onChange: (value: string) => void;
  response: ListData;
  onPatientClick: (patientId: number) => void;
  onSetupClick: (patientId: number) => void;
}

export const SearchPatientModal: React.FC<SearchPatientModalProps> = ({
  isOpen,
  onClose,
  searchValue,
  onChange,
  response,
  onPatientClick,
  onSetupClick,
}) => {
  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (inputRef.current && isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const sortedData = useMemo(() => {
    return response.data?.data?.sort(
      (a: SearchResultItem, b: SearchResultItem) => {
        return (a.resident.status ?? 0) - (b.resident.status ?? 0);
      }
    );
  }, [response.data]);

  const handleSearch = (value: string) => {
    onChange(value);
  };

  const postTag = (item: SearchResultItem) => {
    if (item.resident.status !== 1) {
      return (
        <Tag style={{ margin: 0 }}>
          {item.resident.status === 2 ? "Discharged" : "Leave"}
        </Tag>
      );
    }
    if (isOpen === "bedExit") {
      return (
        <Button
          type="default"
          onClick={(e) => {
            e.stopPropagation();
            onSetupClick(item.resident.id);
          }}
        >
          Setup
        </Button>
      );
    }
    return null;
  };

  return (
    <Modal
      open={isOpen !== null}
      onCancel={onClose}
      footer={null}
      width={742}
      className={styles.searchModal}
      closeIcon={<CloseOutlined className={styles.closeIcon} />}
    >
      <div className={styles.searchContainer}>
        <Input
          placeholder="Search for Patient Name, Room"
          prefix={<SearchOutlined className={styles.searchIcon} />}
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          className={styles.searchInput}
          ref={inputRef}
        />
      </div>
      <div className={styles.patientList}>
        {response.isLoading ? (
          <Spin
            spinning={response.isLoading}
            className={styles.loading}
            indicator={
              <LoadingOutlined
                style={{ fontSize: 48, color: "#161646" }}
                spin
              />
            }
          />
        ) : (
          sortedData?.map((patient: SearchResultItem) => (
            <div
              key={patient.resident.id}
              className={classNames(styles.patientItem)}
              onClick={() => {
                onPatientClick(patient.resident.id);
              }}
            >
              <div className={styles.patientInfo}>
                <span className={styles.patientName}>
                  {patient.resident.name}
                </span>
                <span className={styles.patientCode}>
                  {patient.device.name}
                </span>
              </div>
              {postTag(patient)}
            </div>
          ))
        )}
      </div>
    </Modal>
  );
};
